<template>
	<main class="m-index" :lang="pageLang">
		<PageHeader />
		<TeacherResourceSection />
		<TeacherRecommendSection />
		<TrustSection ref="trustSectionRef" />
	</main>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "~/composables/useI18n";
import { useMasterFortunePageData } from "@variants/seer/master-fortune/composables/useMasterFortunePageData";
import { useMentorListTracking } from "@variants/seer/master-fortune/composables/useMentorListTracking";
import { useIntersectionObserver } from "@vueuse/core";
import { logEvent } from "~/utils/firebase";
import usePixel from "~/hooks/usePixel";
import { customTrackEvent } from "~/utils/customTrackEvent";
import PageHeader from "./components/page-header/index.vue";
import TeacherResourceSection from "./components/teacher-resource-section/index.vue";
import TeacherRecommendSection from "./components/teacher-recommend-section/index.vue";
import TrustSection from "./components/trust-section/index.vue";

useMentorJoinFont();

const I18N_NS = "masterFortune";
const { t, locale, trackTranslationDeps } = useI18n();
const { track, mentorType } = useMentorListTracking({
	deviceType: "mobile",
	mentorType: "normal",
});
const { reportGooglePixelEvent } = usePixel();
const pageLang = computed(() => (locale?.value === "en" ? "en" : "zh-TW"));

useHead(() => {
	trackTranslationDeps();
	return {
		title: t(`${I18N_NS}.meta.title`),
		meta: [
			{ name: "keywords", content: t(`${I18N_NS}.meta.keywords`) },
			{ name: "description", content: t(`${I18N_NS}.meta.description`) },
		],
	};
});

useMasterFortunePageData("mobile");

const route = useRoute();
const trustSectionRef = ref(null);
const trustViewed = ref(false);

onMounted(() => {
	if (import.meta.client) {
		track("mentor_list_page_view", {
			mentor_type: mentorType,
		});
		logEvent("site_realfort_uv");
		reportGooglePixelEvent("gw_consult_page_view");
		customTrackEvent("gw_master_fortune_page_view");
	}

	nextTick(() => {
		if (route.query.to) {
			setTimeout(() => {
				trustSectionRef.value?.sectionRef?.scrollIntoView?.({ behavior: "smooth" });
			}, 800);
		}
	});
});

useIntersectionObserver(
	() => trustSectionRef.value?.sectionRef,
	([entry]) => {
		if (!entry?.isIntersecting || trustViewed.value) return;
		trustViewed.value = true;
		track("mentor_trust_section_view", {
			section_id: "mentor_trust",
			stat_count: 4,
		});
	},
	{ threshold: 0.5 }
);
</script>

<style lang="scss" scoped>
.m-index {
	display: flex;
	flex-direction: column;
	gap: 40px;
	width: 100%;
	padding-bottom: 40px;
	box-sizing: border-box;
	background: #ffffff;
	font-family: "Source Han Serif CN", serif;
}
</style>
